import { useMemo } from "react";

import { getCellValue } from "../utils";
import { CellStatus } from "../primitives";

import { ReportCell, ReportColumn, ColumnType } from "@/types/evaluate";
import { forceJSONString } from "@/utils/strings";
import { useFileContent } from "@/components/ui/FileDisplay/hooks";
import LoadingSpinner from "@/components/LoadingSpinner";
import FileDisplay from "@/components/ui/FileDisplay";

interface FullscreenBasicCellProps {
  cell: ReportCell;
  column: ReportColumn;
}

export const FullscreenBasicCell = ({ cell, column }: FullscreenBasicCellProps) => {
  const displayValue = useMemo(
    () => String(getCellValue(cell, column.column_type)),
    [cell, column.column_type],
  );

  const { fileItems, isFileContent, isProcessingFile } = useFileContent(displayValue);

  if (cell.status === CellStatus.FAILED) {
    return <div className="whitespace-pre-wrap text-sm">{cell.error_message}</div>;
  }

  if (isProcessingFile) {
    return (
      <div className="flex h-full items-center justify-center p-2">
        <LoadingSpinner size={3} />
      </div>
    );
  }

  if (isFileContent && fileItems && fileItems.length > 0) {
    return (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,300px))] gap-2">
        {fileItems.map(item => (
          <FileDisplay
            key={item.data}
            data={item.data}
            className="max-h-[100px] w-full"
            detectedFile={item.fileType}
          />
        ))}
      </div>
    );
  }

  switch (cell.display_value?.type) {
    case "output_media": {
      const media = cell.display_value.value as { url?: string; media_type?: string };
      if (media?.url && (media.media_type === "image" || !media.media_type)) {
        return (
          <div className="flex items-center justify-center p-4">
            <img
              src={media.url}
              alt="Generated"
              className="max-h-[70vh] max-w-full object-contain"
            />
          </div>
        );
      }
      return <div className="text-sm text-gray-500">[Image]</div>;
    }

    case "string":
      return <div className="whitespace-pre-wrap text-sm">{cell.display_value.value}</div>;

    case "number": {
      const value =
        column.column_type === ColumnType.ABSOLUTE_NUMERIC_DISTANCE ||
        column.column_type === ColumnType.COSINE_SIMILARITY
          ? Number(cell.display_value.value).toFixed(2)
          : cell.display_value.value;
      return <div className="text-sm">{value}</div>;
    }

    case "object":
      return (
        <pre className="whitespace-pre-wrap text-sm text-gray-800">
          {forceJSONString(cell.display_value.value)}
        </pre>
      );

    case "unknown":
      return <span className="flex items-center justify-center p-2 text-gray-300">—</span>;

    default: {
      const fallbackValue = getCellValue(cell, column.column_type);
      return <div className="whitespace-pre-wrap text-sm">{fallbackValue || "—"}</div>;
    }
  }
};
